import {
  Button,
  Page,
  SearchInput,
  SimplePagination,
  Table,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import {
  NotificationPayloadT,
  Template,
  TemplateNormalized,
  TemplateTypes,
} from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { PureComponent } from 'react';

import { ConfirmModal } from 'components/shared/confirmModal/components/confirmModal';
import { TableLoadingRow } from 'components/shared/tableLoadingRow';
import { TemplatesModal } from 'components/shared/templatesModal/templatesModal';
import { WithTemplatesInjectedProps } from 'hocs/templates/withTemplates';

import TemplatesAddModal from '../../../../../containers/sections/templates/templatesAddModal';
import { TEMPLATE_NAMES } from '../../templates';
import { normalizeTemplate } from '../../utils/templates.utils';
import { TemplatesSortingDropdown } from '../templatesSortingDropdown';
import { TemplatesTableRow } from '../templatesTableRow';

const FETCH_DELAY = 1000;

export interface TemplatesTabOwnProps {
  templateType: TemplateTypes;
  sectionTitle: string;
  notificationAdd: (payload: NotificationPayloadT) => void;
  hasLanguageTranslationControlFF: boolean;
}

type TemplatesTabStateProps = {};

type TemplatesTabState = {
  isConfirmModalOpen: boolean;
  isAddModalOpen: boolean;
  currentTemplate?: TemplateNormalized;
  waitingForFetch: boolean;
};

export type TemplatesTabProps = WithTemplatesInjectedProps &
  TemplatesTabOwnProps &
  TemplatesTabStateProps;

export class TemplatesTab extends PureComponent<
  TemplatesTabProps,
  TemplatesTabState
> {
  constructor(props: TemplatesTabProps) {
    super(props);

    this.state = {
      isConfirmModalOpen: false,
      isAddModalOpen: false,
      currentTemplate: undefined,
      waitingForFetch: false,
    };
  }

  componentDidMount() {
    this.props.templates.paramsChange(
      { search: '', type: this.props.templateType },
      0,
    );
  }

  setWaitingForFetch(waitingForFetch: boolean) {
    this.setState({
      waitingForFetch,
    });
  }

  delayedFetchItems() {
    const { templates } = this.props;
    const { getItems } = templates;
    setTimeout(() => {
      getItems().then(() => {
        this.setWaitingForFetch(false);
      });
    }, FETCH_DELAY);
  }

  @boundMethod
  gotoNextPage() {
    this.props.templates.nextPage();
  }

  @boundMethod
  gotoPreviousPage() {
    this.props.templates.previousPage();
  }

  @boundMethod
  showRemoveModal(template: Template) {
    this.setState({
      isConfirmModalOpen: true,
      currentTemplate: normalizeTemplate(template),
    });
  }

  @boundMethod
  hideRemoveModal() {
    this.setState({ isConfirmModalOpen: false });
  }

  @boundMethod
  handleDeleteTemplate() {
    const { templates, notificationAdd, templateType } = this.props;
    const { remove } = templates;
    const { currentTemplate } = this.state;

    ampli.settingsTemplatesRemoveTemplateConfirmed({
      templateType: templateType,
      templateName: currentTemplate?.name ?? '',
    });

    if (currentTemplate?.name) {
      this.setWaitingForFetch(true);
      remove(currentTemplate.id)
        .then((r) => {
          this.setState({ isConfirmModalOpen: false });
          const notificationOptions = {
            title: 'Template deleted',
            text: `"${currentTemplate.name}" template has been deleted`,
            icon: 'i-done',
            className: 'success',
            time: 5000,
          };

          notificationAdd(notificationOptions);
          this.delayedFetchItems();
        })
        .catch((e) => {
          const notificationOptions = {
            title: 'An error occurred',
            text: `An error occurred when trying to delete the "${currentTemplate.name}" template`,
            icon: 'i-exclamation',
            className: 'danger',
          };

          notificationAdd(notificationOptions);
          this.setWaitingForFetch(false);
          console.error('Delete error', e);
        });
    }
  }

  @boundMethod
  showAddModal() {
    ampli.settingsTemplatesAddTemplateButtonClicked({
      templateType: this.props.templateType,
    });

    this.setState({
      isAddModalOpen: true,
    });
  }

  @boundMethod
  showEditModal(template: Template) {
    this.setState({
      isAddModalOpen: true,
      currentTemplate: normalizeTemplate(template),
    });
  }

  @boundMethod
  handleAddModalClose(canceled = false) {
    this.setState({
      isAddModalOpen: false,
      currentTemplate: undefined,
    });

    if (!canceled) {
      this.setWaitingForFetch(true);
      this.delayedFetchItems();
    }
  }

  @boundMethod
  handleSearchSubmit(search: string) {
    this.props.templates.paramsChange({ search }, 0);
  }

  @boundMethod
  handleSearchToggle(isOpen: boolean) {
    if (!isOpen && this.props.templates.params.search) {
      this.props.templates.paramsChange({ search: '' }, 0);
    }
  }

  @boundMethod
  handleRetry() {
    this.props.templates.getItems();
  }

  getSingleTdColSpan() {
    return this.props.hasLanguageTranslationControlFF ? 5 : 4;
  }

  renderLoadingStatus() {
    const { templates } = this.props;

    if (templates.getItemsAsyncStatus === 'failed') {
      return (
        <Table.Tr>
          <Table.Td colSpan={this.getSingleTdColSpan()}>
            Something went wrong...
            <Button variant="tertiary" onClick={this.handleRetry}>
              Try again
            </Button>
          </Table.Td>
        </Table.Tr>
      );
    }

    return <TableLoadingRow colSpan={this.getSingleTdColSpan()} />;
  }

  renderSuccessStatus() {
    const { templates, hasLanguageTranslationControlFF, templateType } =
      this.props;
    const { items } = templates;

    if (items.length > 0) {
      return items.map((template, i) => (
        <TemplatesTableRow
          key={i}
          template={template}
          templatesOpenRemoveModal={this.showRemoveModal}
          templatesOpenAddModal={this.showEditModal}
          hasLanguageTranslationControlFF={hasLanguageTranslationControlFF}
          templateType={templateType}
        />
      ));
    }

    return (
      <Table.Tr>
        <Table.Td colSpan={this.getSingleTdColSpan()}>
          No templates found.
        </Table.Td>
      </Table.Tr>
    );
  }

  renderFoot() {
    const { templates } = this.props;
    const { hasNextPage, hasPreviousPage, getItemsAsyncStatus } = templates;

    const foot = (
      <Table.Tfoot>
        <Table.Tr>
          <Table.Td colSpan={this.getSingleTdColSpan()}>
            <SimplePagination
              previousButtonClick={this.gotoPreviousPage}
              previousButtonDisabled={!hasPreviousPage}
              nextButtonClick={this.gotoNextPage}
              nextButtonDisabled={!hasNextPage}
              loading={getItemsAsyncStatus === 'loading'}
            />
          </Table.Td>
        </Table.Tr>
      </Table.Tfoot>
    );

    return foot;
  }

  render() {
    const {
      sectionTitle,
      templates,
      templateType,
      hasLanguageTranslationControlFF,
    } = this.props;
    const { currentTemplate, isAddModalOpen, isConfirmModalOpen } = this.state;
    const { hasNextPage, hasPreviousPage, getItemsAsyncStatus } = templates;
    const { waitingForFetch } = this.state;
    const showFoot = hasNextPage || hasPreviousPage;

    return (
      <>
        <Page.Section
          title={sectionTitle}
          controls={
            <>
              <SearchInput
                onSubmit={this.handleSearchSubmit}
                onToggle={this.handleSearchToggle}
              />

              <TemplatesSortingDropdown
                templatesParamsChange={templates.paramsChange}
                templateType={templateType}
                disabled={!!templates.params.search}
              />

              <Button onClick={this.showAddModal}>
                {`Add ${TEMPLATE_NAMES[templateType].toLowerCase()}`}
              </Button>
            </>
          }
        >
          <Table framed stickyHeader stickyFooter headerWithBackground>
            <Table.Thead>
              <Table.Tr>
                <Table.Th width="30%">Name</Table.Th>
                <Table.Th width="20%">Created</Table.Th>
                <Table.Th width="20%">Updated</Table.Th>
                {hasLanguageTranslationControlFF && (
                  <Table.Th width="30%">Languages</Table.Th>
                )}
                <Table.Th />
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {!waitingForFetch && getItemsAsyncStatus === 'succeeded'
                ? this.renderSuccessStatus()
                : this.renderLoadingStatus()}
            </Table.Tbody>

            {showFoot && this.renderFoot()}
          </Table>
        </Page.Section>

        {isAddModalOpen ? (
          hasLanguageTranslationControlFF ? (
            <TemplatesModal
              isOpen={isAddModalOpen}
              currentTemplate={currentTemplate}
              onClose={this.handleAddModalClose}
              type={templateType}
              initialValues={currentTemplate}
            />
          ) : (
            <TemplatesAddModal
              isAddModalOpen={isAddModalOpen}
              currentTemplate={currentTemplate as any} // we'll remove `TemplatesAddModal` soonish
              onClose={this.handleAddModalClose}
              initialValues={currentTemplate as any} // we'll remove `TemplatesAddModal` soonish
            />
          )
        ) : null}

        <ConfirmModal
          isShown={isConfirmModalOpen}
          headline="Are you sure?"
          text={`Are you sure you want to remove the template
        "${currentTemplate?.name}"?`}
          confirmText="Yes, delete it now"
          cancelText="Cancel"
          confirm={this.handleDeleteTemplate}
          cancel={this.hideRemoveModal}
        />
      </>
    );
  }
}
